/**
 * Friends Context
 * Provides friends state and actions to all components
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getWebSocketAuthMessage, getApiHeaders, API_URL, WS_URL as CONFIG_WS_URL } from '../config/apiConfig';
import { isTokenValid, needsTokenRefresh } from '../auth/tokenManager';
import { playSoundEffect } from '../hooks/useSoundEffects';
import { useNotifications } from '../notifications';
import {
  mergeAttackInitiatedWsPatch,
  mergeAttackCompleteWsPatch,
} from '../utils/mergeAttackWsPatch';

const FriendsContext = createContext(null);

// API and WebSocket URLs - use environment variables, fall back to localhost
const API_BASE = API_URL;
const WS_URL = CONFIG_WS_URL;

export function FriendsProvider({ children }) {
  const { t } = useTranslation();
  const { showNotification, NotificationComponent } = useNotifications();
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ sent: [], received: [] });
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadBySender, setUnreadBySender] = useState({});
  const [pendingChallenges, setPendingChallenges] = useState({ sent: [], received: [] });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialTab, setInitialTab] = useState(null); // 'friends' | 'requests' | 'challenges' | null
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatWithUser, setChatWithUser] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  
  // All refs at the top level (must be declared before useState that uses them)
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const chatWithUserRef = useRef(null);
  const isChatOpenRef = useRef(false);
  const isSpectatingRef = useRef(false); // ✅ SPECTATOR FIX: Track spectating state for emote handler
  const refreshIntervalRef = useRef(null); // ✅ FRIENDS FIX: Track periodic refresh interval
  const friendsRef = useRef([]); // ✅ SPECTATE FIX: Store friends in ref for WebSocket handler access
  
  // Spectating state (Phase 3) - restore from sessionStorage if available
  const [isSpectating, setIsSpectating] = useState(() => {
    try {
      const stored = sessionStorage.getItem('spectating');
      const spectating = stored ? JSON.parse(stored).isSpectating : false;
      isSpectatingRef.current = spectating; // ✅ SPECTATOR FIX: Initialize ref
      return spectating;
    } catch { 
      isSpectatingRef.current = false; // ✅ SPECTATOR FIX: Initialize ref
      return false; 
    }
  });
  const [spectatingGameId, setSpectatingGameId] = useState(() => {
    try {
      const stored = sessionStorage.getItem('spectating');
      return stored ? JSON.parse(stored).gameId : null;
    } catch { return null; }
  });
  const [spectatingPlayerId, setSpectatingPlayerId] = useState(() => {
    try {
      const stored = sessionStorage.getItem('spectating');
      return stored ? JSON.parse(stored).watchingPlayerId : null;
    } catch { return null; }
  });
  const [spectatingGameState, setSpectatingGameState] = useState(() => {
    try {
      const stored = sessionStorage.getItem('spectating');
      return stored ? JSON.parse(stored).gameState : null;
    } catch { return null; }
  });
  // ✅ SPECTATOR FIX: Store pending attack-initiated messages for spectators
  const [pendingAttackInitiated, setPendingAttackInitiated] = useState(null);
  // ✅ SPECTATOR FIX: Store pending emote messages for spectators
  const [pendingEmote, setPendingEmote] = useState(null);
  const [spectators, setSpectators] = useState([]); // Users watching you

  // Keep refs in sync with state
  useEffect(() => {
    chatWithUserRef.current = chatWithUser;
  }, [chatWithUser]);
  
  useEffect(() => {
    isChatOpenRef.current = isChatOpen;
  }, [isChatOpen]);
  
  // ✅ SPECTATE FIX: Keep friendsRef in sync with friends state
  useEffect(() => {
    friendsRef.current = friends;
  }, [friends]);

  const getToken = () => localStorage.getItem('token');
  const getUserId = () => parseInt(localStorage.getItem('userId'));
  
  // Helper function to ensure we have a valid token (refresh if needed)
  const ensureValidToken = useCallback(async () => {
    const token = getToken();
    if (!token) {
      return null;
    }

    // Check if token is valid and doesn't need refresh
    if (isTokenValid(token) && !needsTokenRefresh(token)) {
      return token;
    }

    // Token is expired or about to expire, try to refresh
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      console.warn('[ensureValidToken] No refresh token available');
      return null;
    }

    try {
      const response = await fetch(`${API_BASE}/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        console.error('[ensureValidToken] Token refresh failed:', response.status, response.statusText);
        return null;
      }

      const data = await response.json();
      const newToken = data.token || data.accessToken;
      if (newToken) {
        localStorage.setItem('token', newToken);
        return newToken;
      }
      return null;
    } catch (error) {
      console.error('[ensureValidToken] Error refreshing token:', error);
      return null;
    }
  }, []);
  
  // Track login state to trigger re-initialization after login
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!getToken());

  // Fetch friends list (preserve inGame status from current state)
  const fetchFriends = useCallback(async () => {
    const token = await ensureValidToken();
    if (!token) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/friends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        const newFriends = data.friends || [];
        
        // Preserve inGame and gameId from current state
        setFriends(prev => {
          // ✅ FIX: Create Map with normalized keys (always use numbers) to avoid type mismatch issues
          const prevMap = new Map();
          prev.forEach(f => {
            const key = Number(f.id);
            if (!isNaN(key)) {
              prevMap.set(key, f);
            }
          });
          
          const result = newFriends.map(f => {
            // ✅ FIX: Normalize ID to number for lookup and ensure consistency
            const friendId = Number(f.id);
            if (isNaN(friendId)) {
              console.error('[fetchFriends] Invalid friend ID:', f.id, f);
              return null;
            }
            
            const prevFriend = prevMap.get(friendId);
            
            return {
              ...f,
              // ✅ FIX: Ensure ID is always a number for consistency
              id: friendId,
              inGame: prevFriend?.inGame,
              gameId: prevFriend?.gameId
            };
          }).filter(f => f !== null); // Remove any invalid entries
          
          // ✅ FIX: Prevent losing friends - if API returns fewer friends than we had, something is wrong
          if (prev.length > 0 && result.length < prev.length && newFriends.length < prev.length) {
            const prevIds = new Set(prev.map(f => Number(f.id)));
            const resultIds = new Set(result.map(f => f.id));
            const lost = Array.from(prevIds).filter(id => !resultIds.has(id));
            if (lost.length > 0) {
              // ✅ FIX: If we're losing friends that should still be there, merge with previous state
              lost.forEach(lostId => {
                const lostFriend = prev.find(f => Number(f.id) === lostId);
                if (lostFriend && !resultIds.has(lostId)) {
                  result.push(lostFriend);
                }
              });
            }
          }
          
          // ✅ FIX: Ensure we always return a valid array, never empty if we had friends before
          if (result.length === 0 && prev.length > 0 && newFriends.length === 0) {
            return prev; // Don't clear friends if API returns empty but we had friends
          }
          
          return result;
        });
      } else if (res.status === 401) {
        // Token is invalid, try to refresh and retry once
        const refreshedToken = await ensureValidToken();
        if (refreshedToken) {
          // Retry with refreshed token
          const retryRes = await fetch(`${API_BASE}/friends`, {
            headers: { Authorization: `Bearer ${refreshedToken}` }
          });
          if (retryRes.ok) {
            const data = await retryRes.json();
            const newFriends = data.friends || [];
            setFriends(prev => {
              const prevMap = new Map();
              prev.forEach(f => {
                const key = Number(f.id);
                if (!isNaN(key)) {
                  prevMap.set(key, f);
                }
              });
              
              const result = newFriends.map(f => {
                const friendId = Number(f.id);
                if (isNaN(friendId)) {
                  console.error('[fetchFriends] Invalid friend ID:', f.id, f);
                  return null;
                }
                
                const prevFriend = prevMap.get(friendId);
                return {
                  ...f,
                  id: friendId,
                  inGame: prevFriend?.inGame,
                  gameId: prevFriend?.gameId
                };
              }).filter(f => f !== null);
              
              if (result.length === 0 && prev.length > 0 && newFriends.length === 0) {
                return prev;
              }
              
              return result;
            });
            return; // Success after retry
          }
        }
        const errorText = await res.text();
        console.error('[fetchFriends] API request failed (401 Unauthorized):', errorText);
      } else {
        const errorText = await res.text();
        console.error('[fetchFriends] API request failed:', res.status, res.statusText, errorText);
      }
    } catch (error) {
      console.error('[fetchFriends] Error fetching friends:', error);
    }
  }, [ensureValidToken]);

  // Fetch friend requests
  const fetchRequests = useCallback(async () => {
    const token = await ensureValidToken();
    if (!token) {
      console.warn('[fetchRequests] No valid token available');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/friends/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Merge with existing state to preserve any pending requests that might not be in API yet
        setRequests(prev => {
          const merged = {
            received: data.received || [],
            sent: data.sent || []
          };
          return merged;
        });
      } else if (res.status === 401) {
        // Token is invalid, try to refresh and retry once
        const refreshedToken = await ensureValidToken();
        if (refreshedToken) {
          // Retry with refreshed token
          const retryRes = await fetch(`${API_BASE}/friends/requests`, {
            headers: { Authorization: `Bearer ${refreshedToken}` }
          });
          if (retryRes.ok) {
            const data = await retryRes.json();
            setRequests(prev => {
              const merged = {
                received: data.received || [],
                sent: data.sent || []
              };
              return merged;
            });
            return; // Success after retry
          }
        }
        const errorText = await res.text();
        console.error('[fetchRequests] API request failed (401 Unauthorized):', errorText);
      } else {
        const errorText = await res.text();
        console.error('[fetchRequests] API request failed:', res.status, res.statusText, errorText);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  }, [ensureValidToken]);

  // Search users
  const searchUsers = useCallback(async (query) => {
    const token = getToken();
    if (!token || !query || query.length < 2) return [];

    try {
      const res = await fetch(`${API_BASE}/friends/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ query })
      });
      if (res.ok) {
        const data = await res.json();
        return data.users || [];
      }
    } catch (error) {
      console.error('Error searching users:', error);
    }
    return [];
  }, []);

  // Send friend request
  const sendFriendRequest = useCallback(async (toUserId) => {
    const token = getToken();
    if (!token) return { success: false, error: 'Not authenticated' };

    try {
      const res = await fetch(`${API_BASE}/friends/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ userId: toUserId })
      });
      const data = await res.json();
      
      if (res.ok && data.request) {
        // Update state immediately (like challenges do)
        setRequests(prev => {
          const sent = prev.sent || [];
          // Avoid duplicates
          const exists = sent.some(r => r.id === data.request.id);
          if (exists) {
            return prev;
          }
          const newState = {
            ...prev,
            sent: [...sent, data.request]
          };
          return newState;
        });
        
        // Also fetch from server to ensure we have the latest data
        await fetchRequests();
        
        // Notify via WebSocket
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'friend-request-sent',
            toUserId,
            request: data.request
          }));
        }
        return { success: true };
      }
      return { success: false, error: data.error };
    } catch (error) {
      console.error('Error sending friend request:', error);
      return { success: false, error: 'Network error' };
    }
  }, [fetchRequests]);

  // Accept friend request
  const acceptFriendRequest = useCallback(async (requestId) => {
    const token = getToken();
    if (!token) return { success: false, error: 'Not authenticated' };

    try {
      const res = await fetch(`${API_BASE}/friends/accept/${requestId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (res.ok) {
        await fetchFriends();
        await fetchRequests();
        // Notify via WebSocket
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'friend-request-accepted',
            fromUserId: data.fromUserId,
            friendship: {
              id: data.fromUserId,
              username: data.fromUsername,
              online: true
            }
          }));
        }
        return { success: true };
      }
      return { success: false, error: data.error };
    } catch (error) {
      console.error('Error accepting friend request:', error);
      return { success: false, error: 'Network error' };
    }
  }, [fetchFriends, fetchRequests]);

  // Cancel/decline friend request
  const cancelFriendRequest = useCallback(async (requestId) => {
    const token = getToken();
    if (!token) return { success: false, error: 'Not authenticated' };

    try {
      const res = await fetch(`${API_BASE}/friends/request/${requestId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        await fetchRequests();
        return { success: true };
      }
      const data = await res.json();
      return { success: false, error: data.error };
    } catch (error) {
      console.error('Error canceling friend request:', error);
      return { success: false, error: 'Network error' };
    }
  }, [fetchRequests]);

  // Remove friend
  const removeFriend = useCallback(async (friendId) => {
    const token = getToken();
    if (!token) return { success: false, error: 'Not authenticated' };

    try {
      const res = await fetch(`${API_BASE}/friends/${friendId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        await fetchFriends();
        return { success: true };
      }
      const data = await res.json();
      return { success: false, error: data.error };
    } catch (error) {
      console.error('Error removing friend:', error);
      return { success: false, error: 'Network error' };
    }
  }, [fetchFriends]);

  // Send chat message
  const sendChatMessage = useCallback((toUserId, message) => {
    console.log('[CHAT] sendChatMessage called:', { toUserId, messageLength: message?.length, wsReady: wsRef.current?.readyState });
    
    if (!wsRef.current) {
      console.error('[CHAT] WebSocket ref is null');
      return false;
    }
    
    if (wsRef.current.readyState !== WebSocket.OPEN) {
      console.error(`[CHAT] WebSocket not connected, readyState: ${wsRef.current.readyState}`);
      return false;
    }

    // ✅ FIX: Ensure toUserId is a number (database IDs are numbers)
    const toUserIdNum = Number(toUserId);
    if (isNaN(toUserIdNum)) {
      console.error('[CHAT] Invalid toUserId:', toUserId);
      return false;
    }

    const messageData = {
      type: 'chat-message',
      toUserId: toUserIdNum,
      message
    };
    
    console.log('[CHAT] Sending chat message:', messageData);
    try {
      wsRef.current.send(JSON.stringify(messageData));
      console.log('[CHAT] Message sent successfully');
      return true;
    } catch (error) {
      console.error('[CHAT] Error sending message:', error);
      return false;
    }
  }, []);

  // Load chat history
  const loadChatHistory = useCallback((withUserId) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'chat-history',
      withUserId
    }));
  }, []);

  // Mark messages as read
  const markAsRead = useCallback((fromUserId) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    wsRef.current.send(JSON.stringify({
      type: 'mark-read',
      fromUserId
    }));
  }, []);

  // Open chat with a user
  const openChat = useCallback((user) => {
    setChatWithUser(user);
    setChatMessages([]);
    setIsChatOpen(true);
    loadChatHistory(user.id);
    markAsRead(user.id);
  }, [loadChatHistory, markAsRead]);

  // Close chat
  const closeChat = useCallback(() => {
    setIsChatOpen(false);
  }, []);

  // Send a challenge to a friend
  const sendChallenge = useCallback((toUserId, strategyId) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return false;
    }

    wsRef.current.send(JSON.stringify({
      type: 'challenge-send',
      toUserId,
      strategyId
    }));
    return true;
  }, []);

  // Accept a challenge
  const acceptChallenge = useCallback((challengeId, strategyId) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return false;
    }

    wsRef.current.send(JSON.stringify({
      type: 'challenge-accept',
      challengeId,
      strategyId
    }));
    return true;
  }, []);

  // Cancel/decline a challenge
  const cancelChallenge = useCallback((challengeId) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return false;
    }

    wsRef.current.send(JSON.stringify({
      type: 'challenge-cancel',
      challengeId
    }));
    return true;
  }, []);

  // Fetch pending challenges
  const fetchChallenges = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    wsRef.current.send(JSON.stringify({
      type: 'get-challenges'
    }));
  }, []);

  // ============================================
  // SPECTATING FUNCTIONS (Phase 3)
  // ============================================
  
  // Start spectating a friend's game
  const startSpectating = useCallback((friendId) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return false;
    }

    wsRef.current.send(JSON.stringify({
      type: 'spectate-start',
      friendId
    }));
    return true;
  }, []);

  // Stop spectating
  const stopSpectatingRef = useRef(false);
  const stopSpectating = useCallback(() => {
    // Prevent multiple clicks
    if (stopSpectatingRef.current) {
      console.log('⚠️ Stop spectating already in progress, ignoring duplicate call');
      return;
    }
    
    stopSpectatingRef.current = true;
    console.log('🛑 Stop spectating called, gameId:', spectatingGameId);
    
    // Send stop message if connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && spectatingGameId) {
      try {
        wsRef.current.send(JSON.stringify({
          type: 'spectate-stop',
          gameId: spectatingGameId
        }));
        console.log('✅ Sent spectate-stop message for gameId:', spectatingGameId);
      } catch (error) {
        console.error('❌ Error sending spectate-stop message:', error);
      }
    } else {
      console.warn('⚠️ WebSocket not connected or no gameId:', {
        wsOpen: wsRef.current?.readyState === WebSocket.OPEN,
        gameId: spectatingGameId
      });
    }
    
    // Clear state and sessionStorage
    try {
      sessionStorage.removeItem('spectating');
      setIsSpectating(false);
      isSpectatingRef.current = false; // ✅ SPECTATOR FIX: Update ref
      setSpectatingGameId(null);
      setSpectatingPlayerId(null);
      setSpectatingGameState(null);
    } catch (error) {
      console.error('❌ Error clearing spectator state:', error);
    }
    
    // Navigate back to welcome page after a short delay to ensure message is sent
    setTimeout(() => {
      const isElectron = window.location.protocol === 'file:';
      if (isElectron) {
        window.location.hash = '#/welcome';
      } else {
        window.location.href = '/welcome';
      }
    }, 100);
    
    return true;
  }, [spectatingGameId]);

  // Check if a friend is in a game
  const checkFriendGameStatus = useCallback((friendId) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    wsRef.current.send(JSON.stringify({
      type: 'get-friend-game-status',
      friendId: Number(friendId)
    }));
  }, []);

  // ✅ FIX: Watch for login state changes
  // This ensures we initialize the WebSocket after login, not just on mount
  useEffect(() => {
    const checkLoginState = () => {
      const hasToken = !!getToken();
      setIsLoggedIn(prev => {
        if (prev !== hasToken) {
          // Login state changed - reset initialization flag so we can reinitialize
          if (hasToken && !prev) {
            hasInitializedRef.current = false;
          }
          return hasToken;
        }
        return prev;
      });
    };
    
    // Check periodically for login state changes (handles login redirect)
    const interval = setInterval(checkLoginState, 500);
    
    // Also check on storage events (for cross-tab login)
    window.addEventListener('storage', checkLoginState);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkLoginState);
    };
  }, []);

  // ✅ Extract WebSocket creation logic so it can be reused by forceReconnect
  const createWebSocket = useCallback(() => {
      const token = getToken();
      if (!token) return;

      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = async () => {
        setIsConnected(true);
        
        // ✅ FRIENDS FIX: Ensure we have a valid token before authenticating
        const validToken = await ensureValidToken();
        if (!validToken) {
          console.error('[FRIENDS] No valid token available for WebSocket auth');
          ws.close();
          return;
        }
        
        // ✅ FRIENDS FIX: Re-fetch friends list when WebSocket reconnects
        // This ensures we get fresh online status after reconnection
        fetchFriends();
        
        // Authenticate with API key using valid token
        ws.send(JSON.stringify(getWebSocketAuthMessage(validToken)));

        // Request unread counts and challenges after auth
        setTimeout(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'get-unread' }));
            ws.send(JSON.stringify({ type: 'get-challenges' }));
            
            // ✅ SPECTATE FIX: Check game status for all online friends when WebSocket connects
            // This ensures the spectate button appears even if modal was opened before WebSocket connected
            try {
              const currentFriends = friendsRef.current;
              if (currentFriends && currentFriends.length > 0) {
                currentFriends.filter(f => f.online).forEach(friend => {
                  try {
                    ws.send(JSON.stringify({
                      type: 'get-friend-game-status',
                      friendId: Number(friend.id)
                    }));
                  } catch (err) {
                    console.error(`[WebSocket Connect] Error checking game status for friend ${friend.id}:`, err);
                  }
                });
              }
            } catch (err) {
              console.error('[WebSocket Connect] Error in game status check loop:', err);
            }
            
            // Re-establish spectating session if we were spectating (from sessionStorage)
            try {
              const stored = sessionStorage.getItem('spectating');
              if (stored) {
                const spectateData = JSON.parse(stored);
                if (spectateData.isSpectating && spectateData.watchingPlayerId) {
                  ws.send(JSON.stringify({
                    type: 'spectate-start',
                    friendId: spectateData.watchingPlayerId
                  }));
                }
              }
            } catch (e) {
              console.error('Error re-establishing spectating:', e);
            }
          }
        }, 100);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Special logging for friend-request-received
          if (data.type === 'friend-request-received') {
          }
          
          switch (data.type) {
            case 'error':
              // ✅ FIX: Handle WebSocket error messages
              console.error('[FRIENDS WS] Error received from server:', data.error || data.message || data);
              // Don't break - let it fall through to show the error
              break;
              
            case 'chat-message': {
              console.log('[CHAT] Received chat-message:', { fromUserId: data.fromUserId, messageLength: data.message?.length, timestamp: data.timestamp });
              
              const newMessage = {
                id: Date.now(),
                fromUserId: Number(data.fromUserId),
                toUserId: getUserId(),
                message: data.message,
                timestamp: data.timestamp
              };
              
              const currentChatUser = chatWithUserRef.current;
              const chatIsOpen = isChatOpenRef.current;
              const currentUserId = getUserId();
              
              console.log('[CHAT] Processing message:', {
                fromUserId: data.fromUserId,
                currentUserId,
                chatIsOpen,
                currentChatUserId: currentChatUser?.id,
                matches: chatIsOpen && currentChatUser && Number(currentChatUser.id) === Number(data.fromUserId)
              });
              
              if (chatIsOpen && currentChatUser && Number(currentChatUser.id) === Number(data.fromUserId)) {
                console.log('[CHAT] Adding message to open chat');
                setChatMessages(prev => [...prev, newMessage]);
                if (ws.readyState === WebSocket.OPEN) {
                  ws.send(JSON.stringify({
                    type: 'mark-read',
                    fromUserId: Number(data.fromUserId)
                  }));
                }
              } else {
                console.log('[CHAT] Chat not open or different user, updating unread count');
                // Play message sound for new incoming messages
                playSoundEffect('/sounds/message.mp3');
                setUnreadCount(data.unreadCount || 0);
                setUnreadBySender(prev => ({
                  ...prev,
                  [data.fromUserId]: (prev[data.fromUserId] || 0) + 1
                }));
              }
              break;
            }

            case 'chat-sent': {
              console.log('[CHAT] Received chat-sent confirmation:', { toUserId: data.toUserId, messageLength: data.message?.length });
              const currentChatUser = chatWithUserRef.current;
              const chatIsOpen = isChatOpenRef.current;
              const toUserIdNum = Number(data.toUserId);
              
              console.log('[CHAT] chat-sent processing:', {
                toUserId: toUserIdNum,
                chatIsOpen,
                currentChatUserId: currentChatUser?.id,
                matches: chatIsOpen && currentChatUser && Number(currentChatUser.id) === toUserIdNum
              });
              
              // Only add to chat if the chat is open with the recipient
              if (chatIsOpen && currentChatUser && Number(currentChatUser.id) === toUserIdNum) {
                const sentMessage = {
                  id: Date.now(),
                  fromUserId: getUserId(),
                  toUserId: toUserIdNum,
                  message: data.message,
                  timestamp: data.timestamp
                };
                console.log('[CHAT] Adding sent message to open chat:', sentMessage);
                setChatMessages(prev => {
                  // Check if message already exists (avoid duplicates)
                  const exists = prev.some(msg => 
                    msg.fromUserId === sentMessage.fromUserId &&
                    msg.toUserId === sentMessage.toUserId &&
                    msg.message === sentMessage.message &&
                    Math.abs(msg.timestamp - sentMessage.timestamp) < 1000 // Within 1 second
                  );
                  if (exists) {
                    console.log('[CHAT] Message already exists, skipping duplicate');
                    return prev;
                  }
                  const updated = [...prev, sentMessage];
                  console.log('[CHAT] Updated chat messages, new count:', updated.length);
                  return updated;
                });
              } else {
                console.log('[CHAT] Chat not open with recipient, message stored but not displayed');
              }
              break;
            }

            case 'chat-history':
              console.log('[CHAT] Received chat-history:', { 
                withUserId: data.withUserId, 
                messageCount: data.messages?.length || 0,
                unreadCount: data.unreadCount 
              });
              setChatMessages(data.messages || []);
              setUnreadCount(data.unreadCount || 0);
              console.log('[CHAT] Chat history loaded, messages set');
              break;

            case 'unread-counts':
              setUnreadCount(data.total || 0);
              setUnreadBySender(data.bySender || {});
              break;

            case 'friend-online': {
              // Convert both to numbers for comparison (database IDs are numbers)
              const userId = Number(data.userId);
              if (isNaN(userId)) {
                console.error('[friend-online] Invalid userId:', data.userId);
                break;
              }
              const friend = friendsRef.current.find(f => Number(f.id) === userId);
              const displayName = friend?.username || 'A friend';
              const wasAlreadyOnline = friend?.online === true;
              setFriends(prev => {
                const friendExists = prev.some(f => Number(f.id) === userId);
                if (!friendExists) {
                  setTimeout(() => fetchFriends(), 100);
                  return prev;
                }
                const updated = prev.map(f => {
                  const friendId = Number(f.id);
                  if (friendId === userId) {
                    if (f.online !== true) {
                      return { ...f, online: true };
                    }
                    return { ...f };
                  }
                  return f;
                });
                if (updated.length !== prev.length) {
                  console.error(`[friend-online] ⚠️ CRITICAL: Friend count changed! ${prev.length} → ${updated.length}. Returning previous state.`);
                  return prev;
                }
                return updated;
              });
              if (friend && !wasAlreadyOnline) {
                showNotification(t('friends.friendCameOnline', { username: displayName }), 'info', 4000);
              }
              break;
            }

            case 'friend-offline':
              // Convert both to numbers for comparison (database IDs are numbers)
              setFriends(prev => {
                const userId = Number(data.userId);
                if (isNaN(userId)) {
                  console.error('[friend-offline] Invalid userId:', data.userId);
                  return prev;
                }
                
                // Check if friend exists in current list
                const friendExists = prev.some(f => Number(f.id) === userId);
                
                if (!friendExists) {
                  // Friend not in list yet - refresh friends list to get them
                  setTimeout(() => fetchFriends(), 100);
                  return prev;
                }
                
                // ✅ FIX: Force update by creating new array reference even if value didn't change
                const updated = prev.map(f => {
                  const friendId = Number(f.id);
                  if (friendId === userId) {
                    // Only update if online status actually changed (force re-render)
                    if (f.online !== false) {
                      return { ...f, online: false };
                    }
                    // Force new object reference to trigger React re-render
                    return { ...f };
                  }
                  return f;
                });
                
                // ✅ FIX: Ensure we didn't lose any friends
                if (updated.length !== prev.length) {
                  console.error(`[friend-offline] ⚠️ CRITICAL: Friend count changed! ${prev.length} → ${updated.length}. Returning previous state.`);
                  return prev;
                }
                
                return updated;
              });
              break;

            case 'friend-request-received':
              // Play friend request sound
              playSoundEffect('/sounds/request.mp3');
              // Update state immediately if request data is provided (like challenges do)
              // This ensures the counter updates immediately without waiting for API call
              if (data.request) {
                setRequests(prev => {
                  const received = prev.received || [];
                  // Avoid duplicates - check if request already exists
                  const exists = received.some(r => r.id === data.request.id);
                  if (exists) {
                    return prev; // Already have this request
                  }
                  // Add the request to received list
                  const newState = {
                    ...prev,
                    received: [...received, data.request]
                  };
                  return newState;
                });
              }
              // Also fetch from server to ensure we have the latest data (with proper username)
              // Use a longer delay to ensure immediate state update is visible first
              setTimeout(() => {
                fetchRequests();
              }, 500);
              break;

            case 'friend-added':
              fetchFriends();
              fetchRequests();
              break;

            // Challenge events
            case 'challenge-sent':
              // Our challenge was sent successfully
              setPendingChallenges(prev => ({
                ...prev,
                sent: [...prev.sent, data.challenge]
              }));
              break;

            case 'challenge-received':
              // Play challenge sound
              playSoundEffect('/sounds/challenge.mp3');
              // Someone challenged us
              setPendingChallenges(prev => ({
                ...prev,
                received: [...prev.received, data.challenge]
              }));
              break;

            case 'challenge-cancelled':
              // A challenge was cancelled
              setPendingChallenges(prev => ({
                sent: prev.sent.filter(c => c.id !== data.challengeId),
                received: prev.received.filter(c => c.id !== data.challengeId)
              }));
              break;

            case 'challenges-list':
              // Full list of pending challenges
              setPendingChallenges(data.challenges);
              break;

            case 'challenge-completed':
              // ✅ FIX: Challenge was completed (game created) - remove from pending challenges
              // This ensures players can challenge each other again
              setPendingChallenges(prev => ({
                sent: prev.sent.filter(c => c.id !== data.challengeId),
                received: prev.received.filter(c => c.id !== data.challengeId)
              }));
              // Also refresh to ensure state is accurate
              setTimeout(() => {
                fetchChallenges();
              }, 100);
              break;

            case 'pvp-match-ended':
              try {
                window.dispatchEvent(
                  new CustomEvent('coda-pvp-match-ended', { detail: { gameId: data.gameId } })
                );
              } catch (e) {
                console.error('[FRIENDS WS] Error dispatching coda-pvp-match-ended:', e);
              }
              break;

            case 'mission-matched':
            case 'mission-update':
              // War Effort: mission started or updated (second player joined, fortify, battle result, etc.)
              try {
                window.dispatchEvent(new CustomEvent('war-effort-mission-update', { detail: { mission: data.mission } }));
              } catch (e) {
                console.error('[FRIENDS WS] Error dispatching war-effort-mission-update:', e);
              }
              break;

            case 'war-effort-mission-ended':
              // FFA mission has a winner; close battle and show mission-over (navigate to War Effort)
              try {
                window.dispatchEvent(new CustomEvent('war-effort-mission-ended', { detail: { missionId: data.missionId } }));
              } catch (e) {
                console.error('[FRIENDS WS] Error dispatching war-effort-mission-ended:', e);
              }
              break;

            case 'match-found':
              // Engage-enemy queue: MatchmakingProvider shows notice then navigates via lobby WS
              if (data.gameState?.isEngageEnemyQueue === true) {
                break;
              }
              // Friendly match started - navigate to the game
              if (data.gameId) {
                // Use hash-based navigation for Electron (file:// protocol)
                const isElectron = window.location.protocol === 'file:';
                if (isElectron) {
                  window.location.hash = `#/battle/${data.gameId}`;
                } else {
                  window.location.href = `/battle/${data.gameId}`;
                }
              }
              break;

            // ============================================
            // SPECTATING HANDLERS (Phase 3)
            // ============================================
            
            case 'spectate-started':
              // Store spectating info in sessionStorage (survives page reload)
              sessionStorage.setItem('spectating', JSON.stringify({
                isSpectating: true,
                gameId: data.gameId,
                watchingPlayerId: data.watchingPlayerId,
                gameState: data.gameState
              }));
              setIsSpectating(true);
              isSpectatingRef.current = true; // ✅ SPECTATOR FIX: Update ref
              setSpectatingGameId(data.gameId);
              setSpectatingPlayerId(data.watchingPlayerId);
              setSpectatingGameState(data.gameState);
              setIsModalOpen(false); // Close friends modal
              // Only navigate if not already on the battle page
              const isElectron = window.location.protocol === 'file:';
              const currentPath = isElectron ? window.location.hash : window.location.pathname;
              const battlePath = isElectron ? `#/battle/${data.gameId}` : `/battle/${data.gameId}`;
              if (!currentPath.includes('/battle/')) {
                if (isElectron) {
                  window.location.hash = battlePath;
                } else {
                  window.location.href = battlePath;
                }
              }
              break;
              
            case 'spectate-update':
              // Game state update while spectating
              if (data.isSpectator || data.gameState) {
                // Accept update if isSpectator flag is set OR if gameState is present (backwards compatibility)
                // Force a deep clone to ensure React detects the change (gameState is a complex object)
                const newGameState = data.gameState ? JSON.parse(JSON.stringify(data.gameState)) : null;
                
                // ✅ FIX: If game has ended (winner exists), automatically stop spectating
                if (newGameState?.winner) {
                  // Stop spectating automatically
                  setIsSpectating(false);
                  isSpectatingRef.current = false;
                  setSpectatingGameId(null);
                  setSpectatingPlayerId(null);
                  setSpectatingGameState(null);
                  
                  // Clear sessionStorage
                  try {
                    sessionStorage.removeItem('spectating');
                  } catch (e) {
                    console.error('👁️ Error clearing spectating sessionStorage:', e);
                  }
                  
                  // Send stop spectating message to server (cleanup)
                  // Use current state values since we're updating them
                  const currentGameId = spectatingGameId;
                  if (currentGameId && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                    try {
                      wsRef.current.send(JSON.stringify({
                        type: 'spectate-stop',
                        gameId: currentGameId
                      }));
                    } catch (e) {
                      console.error('👁️ Error sending spectate-stop on game end:', e);
                    }
                  }
                  
                  // Navigate to BattleOverview (not after-battle screen)
                  // Use a small delay to ensure state is cleared
                  setTimeout(() => {
                    if (window.location.pathname.includes('/battle/')) {
                      window.location.href = '/battleoverview';
                    }
                  }, 100);
                  
                  break; // Don't update game state, we're leaving
                }
                
                setSpectatingGameState(prevState => {
                  // Force update by comparing stateVersion or always returning new object
                  if (!prevState || prevState.stateVersion !== newGameState?.stateVersion) {
                    return newGameState;
                  }
                  // Even if version is same, return new object to force re-render
                  return newGameState;
                });
                // Update sessionStorage so state persists
                try {
                  const stored = JSON.parse(sessionStorage.getItem('spectating') || '{}');
                  stored.gameState = newGameState;
                  sessionStorage.setItem('spectating', JSON.stringify(stored));
                } catch (e) { 
                  console.error('👁️ Error updating sessionStorage:', e);
                }
              } else {
                console.warn('👁️ spectate-update received but isSpectator is false and no gameState');
              }
              break;
              
            case 'spectate-stopped':
              setIsSpectating(false);
              isSpectatingRef.current = false; // ✅ SPECTATOR FIX: Update ref
              setSpectatingGameId(null);
              setSpectatingPlayerId(null);
              setSpectatingGameState(null);
              stopSpectatingRef.current = false; // Reset ref in case navigation didn't happen
              break;
              
            case 'emote':
              // ✅ SPECTATOR FIX: Handle emote messages for spectators
              // This works the same way as regular players - emote messages come through the WebSocket
              // For regular players, they come through game WebSocket. For spectators, they come through friends WebSocket.
              
              // ✅ FIX: For spectators, always accept emote messages (same as regular players accept opponent emotes)
              // Regular players accept all emote messages through game WebSocket
              // Spectators should accept all emote messages through friends WebSocket when spectating
              if (isSpectatingRef.current && data.emoteKey) {
                // Store emote info for BattleUI to display (same pattern as regular players)
                setPendingEmote({
                  fromPlayerId: data.fromPlayerId,
                  emoteType: data.emoteType,
                  emoteKey: data.emoteKey,
                  timestamp: Date.now()
                });
              } else if (data.isSpectator && data.emoteKey) {
                // Fallback: also accept if explicitly marked as spectator emote
                setPendingEmote({
                  fromPlayerId: data.fromPlayerId,
                  emoteType: data.emoteType,
                  emoteKey: data.emoteKey,
                  timestamp: Date.now()
                });
              }
              break;
              
            case 'attack-initiated':
              // ✅ SPECTATOR FIX: Handle attack-initiated messages for spectators
              if (
                (data.gameState || data.gameStateSync === 'attack-initiated-minimal') &&
                (isSpectatingRef.current || data.isSpectator) &&
                (!spectatingGameId || !data.gameId || String(data.gameId) === String(spectatingGameId))
              ) {
                let mergedGameState = null;
                setSpectatingGameState((prevState) => {
                  mergedGameState = mergeAttackInitiatedWsPatch(prevState, data);
                  return mergedGameState;
                });
                setPendingAttackInitiated({
                  attackId: data.attackId,
                  attackOutcome: data.attackOutcome,
                  gameState: mergedGameState,
                  yourTurn: false,
                  isBotAttack: data.isBotAttack || false,
                });
              }
              break;

            case 'attack-complete':
              // ✅ SPECTATOR FIX: Apply post-damage state immediately for friend spectators.
              // Without this, HP waits for a later spectate-update tick and appears delayed.
              if (
                (data.gameState || data.gameStateSync === 'attack-complete-minimal') &&
                (isSpectatingRef.current || data.isSpectator) &&
                (!spectatingGameId || !data.gameId || String(data.gameId) === String(spectatingGameId))
              ) {
                let mergedGameState = null;
                setSpectatingGameState((prevState) => {
                  mergedGameState = mergeAttackCompleteWsPatch(prevState, data);
                  return mergedGameState;
                });
                try {
                  const stored = JSON.parse(sessionStorage.getItem('spectating') || '{}');
                  stored.gameState = mergedGameState;
                  sessionStorage.setItem('spectating', JSON.stringify(stored));
                } catch (e) {
                  console.error('👁️ Error updating sessionStorage on attack-complete:', e);
                }
              }
              break;
              
            case 'spectator-joined':
              // Someone is now watching you
              setSpectators(prev => {
                // Check if spectator already exists to avoid duplicates
                const exists = prev.some(s => s.odId === data.spectatorId);
                if (exists) {
                  return prev;
                }
                return [...prev, { odId: data.spectatorId, username: data.username }];
              });
              break;
              
            case 'spectator-left':
              // Someone stopped watching you
              handleSpectatorLeftInternal(data);
              break;
              
            case 'friend-game-status':
              // Update friend's in-game status (handle type mismatch)
              const statusFriendId = Number(data.friendId);
              if (isNaN(statusFriendId)) {
                console.error('[friend-game-status] Invalid friendId:', data.friendId);
                break;
              }
              setFriends(prev => {
                const updated = prev.map(f => {
                  const friendId = Number(f.id);
                  return friendId === statusFriendId ? { ...f, inGame: data.inGame, gameId: data.gameId } : f;
                });
                
                // ✅ FIX: Ensure we didn't lose any friends
                if (updated.length !== prev.length) {
                  console.error(`[friend-game-status] ⚠️ CRITICAL: Friend count changed! ${prev.length} → ${updated.length}. Returning previous state.`);
                  return prev;
                }
                
                return updated;
              });
              break;

            default:
              // ✅ SPECTATOR FIX: Also handle emote messages that might not match the case (fallback)
              if (data.type === 'emote' && isSpectatingRef.current && data.emoteKey) {
                setPendingEmote({
                  fromPlayerId: data.fromPlayerId,
                  emoteType: data.emoteType,
                  emoteKey: data.emoteKey,
                  timestamp: Date.now()
                });
              }
              break;
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        // Note: Interval adjustment is handled by the connection-health useEffect
        // which reacts to isConnected state changes
        
        // Reconnect after delay
        if (getToken()) {
          reconnectTimeoutRef.current = setTimeout(() => {
            createWebSocket();
          }, 3000);
        }
      };

      ws.onerror = (error) => {
        console.error('Friends WebSocket error:', error);
      };
  }, [ensureValidToken, fetchFriends, fetchRequests, fetchChallenges, getUserId]);

  // Initialize WebSocket connection
  // ✅ FIX (FriendsOnlineVisibility): Fetch friends list BEFORE creating WebSocket.
  // If WebSocket connects first, friend-online messages can arrive before friends list loads;
  // the handler would operate on an empty array and the update would be lost.
  useEffect(() => {
    // Only initialize once per login session
    if (hasInitializedRef.current) return;
    
    const token = getToken();
    if (!token) return;
    
    hasInitializedRef.current = true;

    const init = async () => {
      try {
        await fetchFriends();
        await fetchRequests();
      } catch (err) {
        console.error('[FRIENDS] Error fetching initial data, continuing with WebSocket:', err);
      }
      createWebSocket();
    };
    init();

    // Note: Periodic refresh interval is managed by the connection-health useEffect
    // to avoid conflicts and ensure proper cleanup

    // Cleanup on unmount
    return () => {
      hasInitializedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [fetchFriends, fetchRequests, isLoggedIn]); // ✅ FIX: Re-run when login state changes

  // Refresh data when modal opens
  useEffect(() => {
    if (isModalOpen) {
      // ✅ FIX: Always fetch when modal opens to ensure fresh data
      fetchFriends();
      fetchRequests();
      // ✅ FIX: Also refresh challenges to ensure accepted challenges are removed
      fetchChallenges();
    }
  }, [isModalOpen, fetchFriends, fetchRequests, fetchChallenges]);

  // Wrapper for setIsModalOpen that can accept an initial tab
  const openModalWithTab = useCallback((tab) => {
    setInitialTab(tab);
    setIsModalOpen(true);
  }, []);

  // Reset initialTab when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      setInitialTab(null);
    }
  }, [isModalOpen]);
  
  // ✅ SPECTATOR FIX: Keep isSpectatingRef in sync with isSpectating state
  useEffect(() => {
    isSpectatingRef.current = isSpectating;
  }, [isSpectating]);

  // ✅ FRIENDS FIX: Expose refreshFriends function for manual refresh
  // This wraps fetchFriends and can be called from UI components
  const refreshFriends = useCallback(async () => {
    console.log('[FRIENDS] Manual refresh triggered');
    console.log('[FRIENDS] fetchFriends function exists:', typeof fetchFriends);
    try {
      console.log('[FRIENDS] Calling fetchFriends...');
      const fetchFriendsResult = await fetchFriends();
      console.log('[FRIENDS] fetchFriends completed, result:', fetchFriendsResult);
      
      console.log('[FRIENDS] Calling fetchRequests...');
      await fetchRequests();
      console.log('[FRIENDS] fetchRequests completed');
      
      console.log('[FRIENDS] Calling fetchChallenges...');
      await fetchChallenges(); // Also refresh challenges
      console.log('[FRIENDS] fetchChallenges completed');
      
      console.log('[FRIENDS] Manual refresh completed successfully');
    } catch (error) {
      console.error('[FRIENDS] Error during manual refresh:', error);
      console.error('[FRIENDS] Error stack:', error.stack);
      throw error; // Re-throw so caller can handle it
    }
  }, [fetchFriends, fetchRequests, fetchChallenges]);

  // ✅ AGGRESSIVE REFRESH: Force WebSocket reconnection and full state rebuild
  // This should reliably restore the "working state" when things are broken
  // Strategy: Close connection, clear state, recreate WebSocket using same logic
  const forceReconnect = useCallback(async () => {
    try {
      // Step 1: Close existing WebSocket connection
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      // Step 2: Clear reconnect timeout if any
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Step 3: Clear connection state
      setIsConnected(false);
      
      // Step 4: Clear all state to force fresh fetch
      setFriends([]);
      setRequests({ sent: [], received: [] });
      setPendingChallenges({ sent: [], received: [] });
      setUnreadCount(0);
      setUnreadBySender({});
      setChatMessages([]);
      
      // Step 5: Wait a moment for cleanup
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Step 6: Ensure valid token
      const validToken = await ensureValidToken();
      if (!validToken) {
        console.error('[FRIENDS] 🔄 No valid token available, cannot reconnect');
        throw new Error('No valid token available');
      }
      
      // Step 7: Create new WebSocket using the same setup function
      createWebSocket();
      
    } catch (error) {
      console.error('[FRIENDS] 🔄 Error during force reconnect:', error);
      throw error;
    }
  }, [ensureValidToken, createWebSocket]);

  // ✅ FRIENDS FIX: Adjust refresh interval based on connection health
  // This is the single source of truth for periodic refresh interval management
  useEffect(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
    
    if (getToken()) {
      // When connected, use normal 30s interval
      // When disconnected, use more frequent 10s interval (set in ws.onclose)
      const interval = isConnected ? 30000 : 10000;
      refreshIntervalRef.current = setInterval(() => {
        if (getToken()) {
          fetchFriends();
        }
      }, interval);
    }
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [isConnected, fetchFriends]);

  // ✅ FIX: Function to handle spectator-joined messages from game WebSocket
  const handleSpectatorJoined = useCallback((data) => {
    setSpectators(prev => {
      // Check if spectator already exists to avoid duplicates
      const exists = prev.some(s => s.odId === data.spectatorId);
      if (exists) {
        return prev;
      }
      return [...prev, { odId: data.spectatorId, username: data.username }];
    });
  }, []);

  // ✅ FIX: Function to handle spectator-left messages from game WebSocket
  const handleSpectatorLeft = useCallback((data) => {
    setSpectators(prev => prev.filter(s => s.odId !== data.spectatorId));
  }, []);

  // Internal function for friends WebSocket handler
  const handleSpectatorLeftInternal = useCallback((data) => {
    setSpectators(prev => prev.filter(s => s.odId !== data.spectatorId));
  }, []);

  const value = {
    friends,
    requests,
    unreadCount,
    unreadBySender,
    pendingChallenges,
    isModalOpen,
    initialTab,
    isChatOpen,
    chatWithUser,
    chatMessages,
    isConnected,
    setIsModalOpen,
    openModalWithTab,
    openChat,
    closeChat,
    searchUsers,
    sendFriendRequest,
    acceptFriendRequest,
    cancelFriendRequest,
    removeFriend,
    sendChatMessage,
    fetchFriends,
    fetchRequests,
    refreshFriends, // ✅ FRIENDS FIX: Expose manual refresh function
    forceReconnect, // ✅ AGGRESSIVE REFRESH: Force WebSocket reconnection and full state rebuild
    // Challenge actions
    sendChallenge,
    acceptChallenge,
    cancelChallenge,
    fetchChallenges,
    // Spectating (Phase 3)
    isSpectating,
    spectatingGameId,
    spectatingPlayerId,
    spectatingGameState,
    pendingAttackInitiated, // ✅ SPECTATOR FIX: Expose pending attack-initiated messages
    setPendingAttackInitiated, // ✅ SPECTATOR FIX: Allow BattleUI to clear after processing
    pendingEmote, // ✅ SPECTATOR FIX: Expose pending emote messages
    setPendingEmote, // ✅ SPECTATOR FIX: Allow BattleUI to clear after processing
    spectators,
    startSpectating,
    stopSpectating,
    checkFriendGameStatus,
    handleSpectatorJoined, // ✅ FIX: Expose function to handle spectator-joined from game WebSocket
    handleSpectatorLeft // ✅ FIX: Expose function to handle spectator-left from game WebSocket
  };

  return (
    <FriendsContext.Provider value={value}>
      {children}
      {NotificationComponent}
    </FriendsContext.Provider>
  );
}

export function useFriends() {
  const context = useContext(FriendsContext);
  if (!context) {
    throw new Error('useFriends must be used within a FriendsProvider');
  }
  return context;
}

export default FriendsContext;
